<script lang="ts" setup>
  import 'gridstack/dist/gridstack.min.css'
  import { onBeforeUnmount, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { GridStack, GridStackWidget } from 'gridstack'
  import GridWidgetProxy from '@/components/dashboard/GridWidgetProxy.vue'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { DashboardProperties, useDashboard } from '@/composables/useDashboard.ts'
  import { useToastSimplified } from '@/composables/useToastSimplified.ts'
  import { useConfigStore } from '@/stores/Config.store.ts'
  import { useVueGridStack } from '@/composables/useVueGridStack.ts'
  import { useInfoboardWidgetBus, useShortcutWidgetBus } from '@/composables/useDashboardWidgetBuses.ts'

  /*interface GridStackWidgetWithComp extends GridStackWidget {
    properties?: Record<string, unknown>
    component?: Component
  }*/

  type GridStackWidgetWithContent = GridStackWidget & { content?: unknown };
  type StoredDashboard = GridStackWidget[] | string

  const toastService = useToastSimplified()

  const shortcutBus = useShortcutWidgetBus()
  const infoboardWidgetBus = useInfoboardWidgetBus()

  //const titrop = useTitrop()
  const dashboard = useDashboard()
  const isEditing = dashboard.isEditing
  const isSaveAllowed = dashboard.isSaveAllowed
  const isSaving = dashboard.isSaving
  const configStore = useConfigStore()
  const { t } = useI18n()
  const getWidgetName = (widget: GridStackWidget) => t(widget.id ?? '')

  const gridElement = ref<HTMLElement>()
  let grid: GridStack

  //const userDashboard = titrop.value.config.user<GridStackWidget[]>('dashboard_settings', dashboard.defaultDashboard!)
  //const userDashboardProperties = titrop.value.config.user('dashboard_properties', {})

  const userDashboard = computed<StoredDashboard>(() => {
    return (
      configStore.$user.dashboard_settings as StoredDashboard | undefined
    ) ?? dashboard.defaultDashboard!
  })

  const userDashboardProperties = computed(() => {
    return (
      configStore.$user.dashboard_properties as DashboardProperties
    ) ?? {} as DashboardProperties
  })

  const multiSelectModel = ref<string[]>([])
  const skipWatcher = ref<boolean>(false)
  const canonicalItems = ref<GridStackWidget[]>([])
  let resizeObserver: ResizeObserver | undefined
  let projectedColumn: number | undefined

  const items = computed<GridStackWidget[]>(() => {
    //compatibility with JSON (old format)
    if (typeof userDashboard.value === 'string') {
      try {
        const parsed = JSON.parse(userDashboard.value)
        return Array.isArray(parsed) ? parsed as GridStackWidget[] : []
      } catch (error) {
        console.error('parsing error userDashboard:', error)
        return []
      }
    }
    return userDashboard.value
  })

  const itemsProperties = ref<DashboardProperties>({})

  const sortedLayoutItems = (layout: GridStackWidget[]): GridStackWidget[] => {
    return [...layout].sort((a, b) => {
      const yDiff = (a.y ?? 0) - (b.y ?? 0)
      return yDiff !== 0 ? yDiff : (a.x ?? 0) - (b.x ?? 0)
    })
  }

  const tabletWidth = (widget: GridStackWidget): number => {
    const width = widget.w ?? 1

    if (!widget.id?.startsWith('shortcut'))
      return width >= 7 ? 8 : 4

    const shortcutType = itemsProperties.value[widget.id]?.type

    if (shortcutType && shortcutType !== 'full')
      return 2

    if (width >= 7)
      return 8

    if (width >= 3)
      return 4

    return 2
  }

  const projectLayoutToColumns = (
    layout: GridStackWidget[],
    columns: number
  ): GridStackWidget[] => {
    if (columns !== 8)
      return layout.map(widget => ({ ...widget }))

    const placedWidgets: Array<{ x: number; y: number; w: number; h: number }> = []

    return sortedLayoutItems(layout).map((widget) => {
      const w = tabletWidth(widget)
      const h = Math.ceil(widget.h ?? 1)
      let x = 0
      let y = 0

      const doesCollide = (candidateX: number, candidateY: number): boolean => {
        return placedWidgets.some(placed => (
          candidateX < placed.x + placed.w &&
          candidateX + w > placed.x &&
          candidateY < placed.y + placed.h &&
          candidateY + h > placed.y
        ))
      }

      while (doesCollide(x, y)) {
        x += w

        if (x + w > columns) {
          x = 0
          y += 1
        }
      }

      const projected = {
        ...widget,
        x,
        y,
        w
      }

      placedWidgets.push({ x, y, w, h })

      return projected
    })
  }

  const currentCanonicalLayout = (): GridStackWidget[] => {
    return canonicalItems.value.length ? canonicalItems.value : items.value
  }

  const removeFromCanonicalLayout = (id: string) => {
    canonicalItems.value = currentCanonicalLayout()
      .filter(widget => widget.id !== id)
      .map(widget => ({ ...widget }))
  }

  const addToCanonicalLayout = (widget: GridStackWidget) => {
    if (currentCanonicalLayout().some(item => item.id === widget.id))
      return

    canonicalItems.value = [
      ...currentCanonicalLayout().map(item => ({ ...item })),
      { ...widget }
    ]
  }

  const readGridLayout = (): GridStackWidget[] => {
    const widgetsList = grid.save(false) as GridStackWidgetWithContent[] | GridStackWidgetWithContent | undefined
    let cleaned: GridStackWidget[] = []

    if (Array.isArray(widgetsList)) {
      cleaned = widgetsList.map(({ content, ...rest }) => rest)
    } else if (widgetsList && typeof widgetsList === 'object') {
      const { content, ...rest } = widgetsList
      cleaned = [rest]
    }

    return cleaned
  }

  const syncCanonicalFromDesktopGrid = () => {
    if (grid.getColumn() !== 12)
      return

    canonicalItems.value = readGridLayout().map(widget => ({ ...widget }))
  }

  const gridBridge = useVueGridStack({
    container: gridElement,
    proxyComponent: GridWidgetProxy,
    getComponent: dashboard.getComponent,
    getProperties: (id) => ({
      id,
      ...(itemsProperties.value[id] ?? {})
    }),
    onRemove: (id) => removeWidgetFromDDValue(id)
  })

  const syncSelectedWidgets = async () => {
    skipWatcher.value = true
    multiSelectModel.value = items.value.map(item => item.id!)
    await nextTick()
    skipWatcher.value = false
  }

  onMounted(async () => {
    itemsProperties.value = dashboard.mergeDashboardConfig(userDashboardProperties.value!)
    canonicalItems.value = items.value.map(widget => ({ ...widget }))

    grid = gridBridge.init({
      float: false,
      cellHeight: '30px',
      minRow: 1,
      disableResize: true,
      disableDrag: true,
      draggable: {
        handle: '.dashboard-widget-drag-handle'
      },
      columnOpts: {
        breakpointForWindow: false,
        breakpoints: [
          { w: 480, c: 2, layout: 'compact' },
          { w: 768, c: 8, layout: 'compact' },
          { w: 1200, c: 12 }
        ],
        columnMax: 12
      }
    })
    await loadGrid()
    projectedColumn = grid.getColumn()
    syncCanonicalFromDesktopGrid()

    grid.on('change dragstop resizestop', () => {
      if (grid.isIgnoreChangeCB())
        return

      syncCanonicalFromDesktopGrid()
    })

    resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (!grid)
          return

        const column = grid.getColumn()
        if (column === projectedColumn)
          return

        projectedColumn = column
        void loadGrid()
      })
    })
    if (gridElement.value)
      resizeObserver.observe(gridElement.value)

    if (isEditing.value && !isSaving.value) {
      await syncSelectedWidgets()
      grid.enableMove(true)
      grid.enableResize(true)
    }
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
  })

  const loadGrid = async (itemsArray?: GridStackWidget[]) => {
    const sourceLayout = itemsArray ?? currentCanonicalLayout()
    const projectedLayout = projectLayoutToColumns(sourceLayout, grid.getColumn())
    grid.load(projectedLayout)
  }

  const settingsAsJSON = (): GridStackWidget[] => {
    if (grid.getColumn() !== 12)
      return canonicalItems.value.map(widget => ({ ...widget }))

    const cleaned = readGridLayout()
    canonicalItems.value = cleaned.map(widget => ({ ...widget }))
    return cleaned
  }

  const onCancelEditClick = async () => {
    isEditing.value = false
    itemsProperties.value = dashboard.mergeDashboardConfig(userDashboardProperties.value!)
    canonicalItems.value = items.value.map(widget => ({ ...widget }))
    grid.removeAll()
    await nextTick()
    await loadGrid(items.value)
  }

  const onSaveEditClick = async () => {
    if (!isSaveAllowed.value || isSaving.value)
      return

    const layout = settingsAsJSON()
    const properties = dashboard.getUserConfigDiff(itemsProperties.value)

    isSaving.value = true
    try {
      await Promise.all([
        configStore.setAsync('dashboard_settings', layout),
        configStore.setAsync('dashboard_properties', properties)
      ])
      isEditing.value = false
      toastService.addPredefined('saveConfirmation')
    } catch (error) {
      console.error('Dashboard saving failed:', error)
      toastService.add({
        severity: 'error',
        life: 5000,
        summary: t('saveError')
      })
    } finally {
      isSaving.value = false
    }
  }

  const removeWidgetFromDDValue = async (widgetID: string) => {
    skipWatcher.value = true
    removeFromCanonicalLayout(widgetID)
    const index = multiSelectModel.value.indexOf(widgetID)
    if (index > -1)
      multiSelectModel.value.splice(index, 1)
    await nextTick()
    skipWatcher.value = false
  }

  const onResetClick = async () => {
    skipWatcher.value = true
    grid.removeAll()
    await nextTick()
    itemsProperties.value = dashboard.defaultDashboardProperties()
    const defaultLayout = dashboard.defaultDashboard ?? []
    canonicalItems.value = defaultLayout.map(widget => ({ ...widget }))
    await loadGrid(defaultLayout)
    multiSelectModel.value = defaultLayout
      .map(widget => widget.id)
      .filter((id): id is string => Boolean(id))
    await nextTick()
    skipWatcher.value = false
  }

  useEventBus('dashboard-edit-cancel').on(() => {
    onCancelEditClick()
    toastService.add({
      severity: 'info',
      life: 2500,
      summary: t('resetOriginal')
    })
  })

  useEventBus('dashboard-edit-save').on(() => {
    void onSaveEditClick()
  })

  useEventBus('dashboard-edit-start').on(() => {
    void syncSelectedWidgets()
  })

  useEventBus('dashboard-reset-click').on(() => {
    onResetClick()
    toastService.add({
      severity: 'info',
      life: 2500,
      summary: t('resetDefault')
    })
  })

  shortcutBus.on(async (widgetData) => {
    const index = items.value.findIndex(item => item.id === widgetData.id)

    if (itemsProperties.value[widgetData.id]) {
      const tempProps = itemsProperties.value[widgetData.id]
      itemsProperties.value[widgetData.id] = { ...tempProps, type: widgetData.type }
    } else {
      itemsProperties.value[widgetData.id] = { type: widgetData.type }
    }

    const widget = grid.engine.nodes.find(x => x.id! === widgetData.id)
    if (index > -1) {
      if (widget) {
        const { h, w, x, y } = widget
        grid.removeWidget(widget.el!)
        await nextTick()
        grid.addWidget({ ...items.value[index], h, w, x, y })
      }
    } else {
      if (widget) {
        await nextTick()
        grid.update(widget.el!, widgetData.type === 'full' ? { w: 4, h: 4 } : { w: 4, h: 2 })
      }
    }
  })

  infoboardWidgetBus.on(async (widgetData) => {
    const raw = toRaw(itemsProperties.value['infoboard'])

    itemsProperties.value['infoboard'] = {
      ...raw,
      items: widgetData.items,
      visibleTypes: [...widgetData.visibleTypes],
      visibleState: widgetData.visibleState
    }

    const widget = grid.engine.nodes.find(x => x.id! === 'infoboard')
    const index = items.value.findIndex(item => item.id === 'infoboard')
    if (widget && index > -1) {
      const { h, w, x, y } = widget
      grid.removeWidget(widget.el!)
      await nextTick(() => grid.addWidget({ ...items.value[index], h, w, x, y }))
    }
  })

  watch([isEditing, isSaving], async ([editing, saving]) => {
    if (editing && !saving) {
      grid.enableMove(true)
      grid.enableResize(true)
    } else {
      await nextTick()
      grid?.enableMove(false)
      grid?.enableResize(false)
    }
  })

  watch(multiSelectModel, (newVal, oldVal) => {
    if (skipWatcher.value) return
    const added = newVal.filter(x => !oldVal.includes(x))
    const removed = oldVal.filter(x => !newVal.includes(x))

    if (added.length > 1 || removed.length > 1) return
    if (removed.length) {
      removeFromCanonicalLayout(removed[0])
      const widget = grid.engine.nodes.find(x => x.id! === removed[0])
      if (widget)
        grid.removeWidget(widget.el!)
    }
    if (added.length) {
      const wasOnInitialBoard = items.value.find(x => x.id! === added[0])
      if (wasOnInitialBoard) {
        addToCanonicalLayout(wasOnInitialBoard)
        grid.addWidget({ ...wasOnInitialBoard! })
      } else {
        const temp = dashboard.defaultDashboard?.find(x => x.id! === added[0])
        if (temp) {
          addToCanonicalLayout(temp)
          //grid.addWidget({ ...temp! })
          grid.addWidget(temp)
        }
      }

      grid.enableMove(true)
      grid.enableResize(true)
    }
  }, { deep: true })

  watchEffect(() => {
    isSaveAllowed.value = multiSelectModel.value.length >= 1
  })
</script>

<template>
  <div class="w-full">
    <div v-if="isEditing" class="mb-2 w-full border-b border-gray-200 pb-3 dark:border-gray-700 px-1 md:px-0">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {{ t('editTitle') }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400">
            {{ t('activeCount', { active: multiSelectModel.length, total: dashboard.defaultDashboard?.length ?? 0 }) }}
          </div>
        </div>

        <FloatLabel variant="on" class="w-full sm:w-80">
          <MultiSelect
            class="w-full"
            :disabled="isSaving"
            :options="dashboard.defaultDashboard!"
            :optionLabel="getWidgetName"
            optionValue="id"
            filter
            :maxSelectedLabels="1"
            v-model="multiSelectModel"
            :showToggleAll="false">
            <template #option="{ option }">
              <div class="flex w-full items-center justify-between gap-4">
                <span>{{ getWidgetName(option) }}</span>
              </div>
            </template>
          </MultiSelect>
          <label>{{ t('widgets') }}</label>
        </FloatLabel>
      </div>

      <Message v-if="multiSelectModel.length < 1" class="mt-2" size="small" severity="warn">
        <template #icon>
          <component is="InfoIcon" />
        </template>
        <p>{{ t('minimumWidgets') }}</p>
      </Message>
    </div>

    <div ref="gridElement" class="grid-stack" />

    <div v-if="isEditing" class="flex w-full justify-center border-t px-1 md:px-0 pt-2 md:justify-end">
      <ButtonGroup class="flex w-full min-w-0 md:w-auto">
        <ButtonDefault
          class="min-w-0 flex-1 md:flex-none"
          icon="ResetIcon"
          :label="t('reset')"
          size="small"
          outlined
          :disabled="isSaving"
          @click="onResetClick" />
        <ButtonDefault
          class="min-w-0 flex-1 md:flex-none"
          icon="CancelIcon"
          :label="t('cancel')"
          size="small"
          outlined
          severity="danger"
          :disabled="isSaving"
          @click="onCancelEditClick" />
        <ButtonDefault
          class="min-w-0 flex-1 md:flex-none"
          icon="SaveIcon"
          :label="t('save')"
          size="small"
          outlined
          :loading="isSaving"
          :disabled="!isSaveAllowed || isSaving"
          @click="onSaveEditClick" />
      </ButtonGroup>
    </div>
  </div>
</template>

<style scoped>
</style>

<i18n>
{
  "de": {
    "editTitle": "Dashboard bearbeiten",
    "activeCount": "{active} von {total} Widgets aktiv",
    "widgets": "Widgets",
    "minimumWidgets": "Mindestens ein Widget muss ausgewählt sein",
    "reset": "Zurücksetzen",
    "cancel": "Abbrechen",
    "save": "Speichern",
    "saveError": "Dashboard konnte nicht gespeichert werden",
    "resetOriginal": "Das Dashboard wurde auf die ursprünglichen Einstellungen zurückgesetzt",
    "resetDefault": "Das Dashboard wurde auf die Standardeinstellungen zurückgesetzt",
    "favorites": "Favoriten",
    "infoboard": "Infoboard",
    "worktime": "Arbeitszeiterfassung",
    "statechange": "Mein Status",
    "editworktime": "Arbeitszeitkorrekturen",
    "timetrackings": "Aktive Time-Trackings",
    "todayhours": "Heutige Zeit",
    "servicerevenue": "Dienstleistungsumsatz",
    "yearrevenue": "Jahresumsatz",
    "shortcut-effort-capture": "Leistungserfassung",
    "shortcut-working-time-capture": "Stempeluhr",
    "shortcut-infoboard-news": "Neueste Beiträge",
    "shortcut-time-tracking": "Time-Tracking",
    "shortcut-team-overview": "Mitarbeiterübersicht"
  },
  "en": {
    "editTitle": "Edit dashboard",
    "activeCount": "{active} of {total} widgets active",
    "widgets": "Widgets",
    "minimumWidgets": "At least one widget must be selected",
    "reset": "Reset",
    "cancel": "Cancel",
    "save": "Save",
    "saveError": "Dashboard could not be saved",
    "resetOriginal": "The dashboard was restored to its original settings",
    "resetDefault": "The dashboard was restored to its default settings",
    "favorites": "Favorites",
    "infoboard": "Infoboard",
    "worktime": "Work time tracking",
    "statechange": "My status",
    "editworktime": "Work time corrections",
    "timetrackings": "Active time trackings",
    "todayhours": "Today's time",
    "servicerevenue": "Service revenue",
    "yearrevenue": "Annual revenue",
    "shortcut-effort-capture": "Effort capture",
    "shortcut-working-time-capture": "Time clock",
    "shortcut-infoboard-news": "Latest posts",
    "shortcut-time-tracking": "Time tracking",
    "shortcut-team-overview": "Employee overview"
  }
}
</i18n>
